import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axiosInstance from '../api/axiosInstance';
import { motion } from 'framer-motion';

export default function NotificationSettings() {
  const [settings, setSettings] = useState({
    emailOnNewPM: false,
    emailOnThreadReply: false,
    emailOnMention: false,
    emailOnThreadSubscription: false // Neue Einstellung für Thread-Abonnements
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await axiosInstance.get('/user-settings/notifications');
      setSettings(res.data.notificationSettings);
      setLoading(false);
    } catch (err) {
      console.error('Error loading notification settings:', err);
      setLoading(false);
    }
  };

  const handleSettingChange = (setting, value) => {
    setSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  const saveSettings = async () => {
    setSaving(true);
    setMessage('');
    
    try {
      await axiosInstance.put('/user-settings/notifications', settings);
      setMessage('Einstellungen erfolgreich gespeichert!');
      setMessageType('success');
    } catch (err) {
      setMessage('Fehler beim Speichern der Einstellungen');
      setMessageType('error');
      console.error('Error saving settings:', err);
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const sendTestEmail = async () => {
    setSendingTest(true);
    setMessage('');
    
    try {
      await axiosInstance.post('/user-settings/test-email');
      setMessage('Test-E-Mail erfolgreich gesendet! Überprüfen Sie Ihr Postfach.');
      setMessageType('success');
    } catch (err) {
      setMessage('Fehler beim Senden der Test-E-Mail');
      setMessageType('error');
      console.error('Error sending test email:', err);
    } finally {
      setSendingTest(false);
      setTimeout(() => setMessage(''), 5000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-base-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#448f65] mx-auto"></div>
          <p className="mt-4 text-gray-600">Lade Benachrichtigungseinstellungen...</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-base-100 py-8"
    >
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="card bg-base-100 shadow-md mb-8">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-base-content flex items-center gap-3">
                  📧 E-Mail-Benachrichtigungen
                </h1>
                <p className="text-base-content/70 mt-2">
                  Verwalten Sie Ihre E-Mail-Benachrichtigungseinstellungen
                </p>
              </div>
              <Link
                to="/usercp"
                className="bg-[#448f65] text-white px-4 py-2 rounded-lg hover:bg-[#2d5f43] transition-colors hover:text-white"
              >
                ← Zurück zum UserCP
              </Link>
            </div>
          </div>
        </div>

        {/* Message Display */}
        {message && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`p-4 rounded-lg mb-6 ${
              messageType === 'success' 
                ? 'bg-green-100 border border-green-400 text-green-700' 
                : 'bg-red-100 border border-red-400 text-red-700'
            }`}
          >
            {message}
          </motion.div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Settings Panel */}
          <div className="lg:col-span-2 card bg-base-200 shadow-md">
            <div className="card-body">
            <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center gap-2">
              ⚙️ Benachrichtigungseinstellungen
            </h2>

            <div className="space-y-6">
              {/* PM Notifications */}
              <div className="p-4 border border-base-300 rounded-lg bg-base-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-base-300 rounded-lg flex items-center justify-center">
                      <span className="text-base-content text-lg">💬</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-base-content">Private Nachrichten</h3>
                      <p className="text-sm text-base-content/70">
                        E-Mail-Benachrichtigung bei neuen privaten Nachrichten
                      </p>
                    </div>
                  </div>
                  <label className="cursor-pointer">
                    <input
                      type="checkbox"
                      className="toggle toggle-primary"
                      checked={settings.emailOnNewPM}
                      onChange={(e) => handleSettingChange('emailOnNewPM', e.target.checked)}
                    />
                  </label>
                </div>
              </div>

              {/* Thread Reply Notifications */}
              <div className="p-4 border border-base-300 rounded-lg bg-base-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-base-300 rounded-lg flex items-center justify-center">
                      <span className="text-base-content text-lg">�</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-base-content">Thread-Abonnements</h3>
                      <p className="text-sm text-base-content/70">
                        E-Mail-Benachrichtigung bei neuen Antworten in abonnierten Threads
                      </p>
                      <p className="text-xs text-base-content/50 mt-1">
                        Aktivieren Sie diese Option, um E-Mails für abonnierte Threads zu erhalten
                      </p>
                    </div>
                  </div>
                  <label className="cursor-pointer">
                    <input
                      type="checkbox"
                      className="toggle toggle-primary"
                      checked={settings.emailOnThreadSubscription}
                      onChange={(e) => handleSettingChange('emailOnThreadSubscription', e.target.checked)}
                    />
                  </label>
                </div>
              </div>

              {/* Mention Notifications */}
              <div className="p-4 border border-base-300 rounded-lg bg-base-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-base-300 rounded-lg flex items-center justify-center">
                      <span className="text-base-content text-lg">@</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-base-content">Erwähnungen</h3>
                      <p className="text-sm text-base-content/70">
                        E-Mail-Benachrichtigung wenn Sie erwähnt werden
                      </p>
                      <p className="text-xs text-base-content/50 mt-1">
                        (Feature wird in Zukunft verfügbar sein)
                      </p>
                    </div>
                  </div>
                  <label className="cursor-pointer">
                    <input
                      type="checkbox"
                      className="toggle toggle-primary"
                      checked={settings.emailOnMention}
                      onChange={(e) => handleSettingChange('emailOnMention', e.target.checked)}
                      disabled
                    />
                  </label>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="mt-8 pt-6 border-t border-base-300">
              <button
                onClick={saveSettings}
                disabled={saving}
                className="bg-[#448f65] text-white px-4 py-2 rounded-lg hover:bg-[#2d5f43] transition-colors hover:text-white disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Speichere...
                  </>
                ) : (
                  <>
                    💾 Einstellungen speichern
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Test Panel */}
          <div className="space-y-6">
            {/* Test Email */}
            <div className="card bg-base-100 shadow-md">
              <div className="card-body">
              <h3 className="text-lg font-semibold text-base-content mb-4 flex items-center gap-2">
                ✉️ Test-E-Mail
              </h3>
              <p className="text-sm text-base-content/70 mb-4">
                Senden Sie eine Test-E-Mail, um zu überprüfen, ob Ihre E-Mail-Adresse korrekt konfiguriert ist.
              </p>
              <button
                onClick={sendTestEmail}
                disabled={sendingTest}
                className="bg-[#448f65] text-white px-4 py-2 rounded-lg hover:bg-[#2d5f43] transition-colors hover:text-white disabled:opacity-50 w-full"
              >
                {sendingTest ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Sende...
                  </>
                ) : (
                  <>
                    📤 Test-E-Mail senden
                  </>
                )}
              </button>
            </div>

            {/* Info Panel */}
            <div className="bg-info/10 border border-info/30 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-base-content mb-3">
                ℹ️ Wichtige Informationen
              </h3>
              <div className="text-sm text-base-content/80 space-y-2">
                <p>
                  • E-Mail-Benachrichtigungen werden nur gesendet, wenn die entsprechende Option aktiviert ist.
                </p>
                <p>
                  • Überprüfen Sie regelmäßig Ihren Spam-Ordner.
                </p>
                <p>
                  • Sie können diese Einstellungen jederzeit ändern.
                </p>
                <p>
                  • Test-E-Mails helfen dabei, die Konfiguration zu überprüfen.
                </p>
              </div>
            </div>

            {/* PM Statistics */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-purple-800 mb-3">
                📊 PM-Statistiken
              </h3>
              <div className="text-sm text-purple-700">
                <p>
                  Aktivieren Sie PM-Benachrichtigungen, um sofort über neue private Nachrichten informiert zu werden.
                </p>
              </div>
            </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
